
import {
  defineComponent, ref, computed, watch, onMounted, onBeforeUnmount, nextTick,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { NestApiType, nestCall } from '@/composables/nestApi';

export default defineComponent({
  name: 'TemplateHtmlPreviewSlider',

  props: {
    sessionId: {
      type: String,
      default: '',
    },

    uid: {
      type: String,
      default: '',
    },

    previewCount: {
      type: Number,
      default: 0,
    },

    previews: {
      type: Array as () => { html: string; label: string }[],
      default: () => [],
    },

    hideMediaToggle: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['close'],

  setup(props) {
    const { t } = useI18n();
    const currentIndex = ref(0);
    const viewMode = ref<'desktop' | 'mobile'>('desktop');
    const loading = ref(false);
    const error = ref('');
    const currentHtml = ref('');

    // Determine mode: direct previews vs fetch-based
    const isDirectMode = computed(() => props.previews && props.previews.length > 0);
    const totalCount = computed(() => (isDirectMode.value ? props.previews.length : props.previewCount));

    // Viewport measurement for phone scaling
    const viewportRef = ref<HTMLElement | null>(null);
    const viewportHeight = ref(0);
    const PHONE_NATIVE_HEIGHT = 820;
    let resizeObserver: ResizeObserver | null = null;

    // Detect if the HTML already contains a phone wrapper (sms/whatsapp/push)
    const hasPhoneWrapper = computed(() => currentHtml.value.includes('spm-phone-wrapper'));

    // Detect if the HTML is a flow SVG preview (not a template)
    const isFlowPreview = computed(() => currentHtml.value.includes('<svg xmlns='));

    // Extract SVG content from flow HTML for direct rendering
    const flowSvgContent = computed(() => {
      if (!isFlowPreview.value) return '';
      const match = currentHtml.value.match(/<svg[\s\S]*<\/svg>/);
      return match ? match[0] : '';
    });

    // Flow zoom/pan state
    const flowScale = ref(1);
    const flowTx = ref(0);
    const flowTy = ref(0);
    const flowDragging = ref(false);
    const flowDragStart = ref({ x: 0, y: 0, tx: 0, ty: 0 });

    const flowCanvasStyle = computed(() => ({
      transform: `translate(${flowTx.value}px, ${flowTy.value}px) scale(${flowScale.value})`,
      transformOrigin: '0 0',
      cursor: flowDragging.value ? 'grabbing' : 'grab',
    }));

    const resetFlowZoom = () => {
      flowFitView();
    };

    const flowZoomIn = () => {
      const ns = Math.min(flowScale.value + 0.2, 5);
      flowScale.value = ns;
    };

    const flowZoomOut = () => {
      const ns = Math.max(flowScale.value - 0.2, 0.1);
      flowScale.value = ns;
    };

    const onFlowWheel = (e: WheelEvent) => {
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      const ns = Math.min(Math.max(flowScale.value + delta, 0.1), 5);
      const ratio = ns / flowScale.value;
      flowTx.value = e.offsetX - (e.offsetX - flowTx.value) * ratio;
      flowTy.value = e.offsetY - (e.offsetY - flowTy.value) * ratio;
      flowScale.value = ns;
    };

    const onFlowMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return;
      flowDragging.value = true;
      flowDragStart.value = {
        x: e.clientX, y: e.clientY, tx: flowTx.value, ty: flowTy.value,
      };
    };

    const onFlowMouseMove = (e: MouseEvent) => {
      if (!flowDragging.value) return;
      flowTx.value = flowDragStart.value.tx + (e.clientX - flowDragStart.value.x);
      flowTy.value = flowDragStart.value.ty + (e.clientY - flowDragStart.value.y);
    };

    const onFlowMouseUp = () => {
      flowDragging.value = false;
    };

    const flowWrapperRef = ref<HTMLElement | null>(null);

    const flowFitView = () => {
      if (!flowWrapperRef.value || !flowSvgContent.value) return;
      const wrapper = flowWrapperRef.value;
      const svgEl = wrapper.querySelector('svg');
      if (!svgEl) return;
      const svgW = svgEl.getAttribute('width');
      const svgH = svgEl.getAttribute('height');
      if (!svgW || !svgH) return;
      const contentW = parseFloat(svgW);
      const contentH = parseFloat(svgH);
      const wrapperW = wrapper.clientWidth;
      const wrapperH = wrapper.clientHeight;
      const padding = 40;
      const scaleX = (wrapperW - padding) / contentW;
      const scaleY = (wrapperH - padding) / contentH;
      const ns = Math.min(scaleX, scaleY, 1);
      flowScale.value = ns;
      flowTx.value = (wrapperW - contentW * ns) / 2;
      flowTy.value = (wrapperH - contentH * ns) / 2;
    };

    // Dynamic scale style for phone previews so the phone fits entirely in the viewport
    const phoneFrameStyle = computed(() => {
      const availableHeight = viewportHeight.value - 40; // account for viewport padding
      if (availableHeight <= 0) return {};
      const scale = Math.min(1, availableHeight / PHONE_NATIVE_HEIGHT);
      return {
        transform: `scale(${scale})`,
        transformOrigin: 'top center',
      };
    });

    // Cache fetched HTML to avoid re-fetching
    const htmlCache = new Map<number, string>();

    const fetchPreview = async (index: number) => {
      // Check cache first
      if (htmlCache.has(index)) {
        currentHtml.value = htmlCache.get(index) || '';
        return;
      }

      loading.value = true;
      error.value = '';
      currentHtml.value = '';

      try {
        const response = await nestCall(
          'post',
          NestApiType.CLIENT_API,
          '/job/import/html-preview',
          {},
          '',
          { sessionId: props.sessionId, uid: props.uid, index },
        );
        if (response?.html) {
          htmlCache.set(index, response.html);
          currentHtml.value = response.html;
        } else {
          error.value = t('import.htmlPreview.notFound');
        }
      } catch {
        error.value = t('import.htmlPreview.fetchError');
      } finally {
        loading.value = false;
      }
    };

    const goTo = (idx: number) => {
      if (idx < 0 || idx >= totalCount.value) return;
      currentIndex.value = idx;
      resetFlowZoom();
    };

    const getTabLabel = (idx: number): string => {
      if (isDirectMode.value && props.previews[idx]) {
        return props.previews[idx].label;
      }
      return idx === 0
        ? t('import.htmlPreview.primary')
        : `${t('import.htmlPreview.redirection')} ${idx}`;
    };

    // Auto fit-view when flow SVG content appears or changes
    watch(flowSvgContent, (val) => {
      if (val) {
        nextTick(() => flowFitView());
      }
    });

    // Force desktop mode when HTML already contains a phone frame
    watch(hasPhoneWrapper, (val) => {
      if (val) {
        viewMode.value = 'desktop';
      }
    });

    // Fetch HTML when currentIndex changes (fetch mode only)
    watch(currentIndex, (newIdx) => {
      if (isDirectMode.value) {
        currentHtml.value = props.previews[newIdx]?.html || '';
      } else {
        fetchPreview(newIdx);
      }
    });

    onMounted(() => {
      if (viewportRef.value) {
        viewportHeight.value = viewportRef.value.clientHeight;
        resizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
          entries.forEach((entry: ResizeObserverEntry) => {
            viewportHeight.value = entry.contentRect.height;
          });
        });
        resizeObserver.observe(viewportRef.value);
      }
      if (isDirectMode.value) {
        currentHtml.value = props.previews[0]?.html || '';
      } else {
        fetchPreview(0);
      }
    });

    onBeforeUnmount(() => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    });

    return {
      t,
      currentIndex,
      viewMode,
      loading,
      error,
      currentHtml,
      hasPhoneWrapper,
      phoneFrameStyle,
      viewportRef,
      totalCount,
      goTo,
      getTabLabel,
      isFlowPreview,
      flowSvgContent,
      flowCanvasStyle,
      resetFlowZoom,
      flowZoomIn,
      flowZoomOut,
      onFlowWheel,
      onFlowMouseDown,
      onFlowMouseMove,
      onFlowMouseUp,
      flowWrapperRef,
      flowFitView,
    };
  },
});
